<template>
  <div class="dashboards-container">
    <div
      v-if="!props.defaultWorkbook || !props.defaultDashboard"
      class="form-block m-b-12"
    >
      <div class="flex some-inputs">
        <div class="flex flex-col m-r-20 input-container">
          <label class="small-label">Воркбук</label>
          <CatalogDataSingleInput
            :useIdOnly="true"
            :selectType="props.type"
            :isSegmentSetted="false"
            :modelValue="workbook.workbook"
            @update:modelValue="
              ($event) => {
                workbook.dashboard = null;
                router.push({
                  name: route.name,
                });
                workbook.workbook = $event;
              }
            "
          ></CatalogDataSingleInput>
        </div>
        <div class="flex flex-col input-container">
          <label class="small-label">Дашборд</label>
          <Dropdown
            v-model="workbook.dashboard"
            optionLabel="name"
            :options="dashboards"
            :disabled="workbook.workbook == null"
            placeholder="Выберите значение"
            filter
          ></Dropdown>
        </div>
        <div
          v-if="!props.hideExpandButton"
          class="flex flex-row m-b-2 m-l-12 flex-end"
        >
          <div
            :title="
              workbook.dashboard == null
                ? 'Сначала выберите дашборд'
                : 'Развернуть дашборд на весь экран'
            "
          >
            <Button
              :disabled="workbook.dashboard == null"
              @click="isFullPageDashboard = true"
              icon="pi pi-window-maximize"
              label="Во весь экран"
              class="expand-button"
              :pt="{
                root: {
                  style: 'min-width: 150px; white-space: nowrap; height: 32px',
                },
              }"
            />
          </div>
        </div>
      </div>

      <div
        class="flex flex-col m-t-12"
        v-if="
          workbook.dashboard != null &&
          workbook.dashboard.description &&
          workbook.dashboard.description.length > 0
        "
      >
        <Accordion class="accord-my" @tab-click="tabClick">
          <AccordionTab header="Описание дашборда">
            <p class="m-0">
              {{ workbook.dashboard.description }}
            </p>
          </AccordionTab>
        </Accordion>
      </div>
    </div>

    <HandlerAccessDenied :is-denied="isDenied">
      <div id="dashboard-container">
        <div
          class="form-block"
          v-if="
            token.length > 0 && workbook.dashboard != null && !isLoadingData
          "
        >
          <iframe
            :src="getToken()"
            frameborder="0"
            :width="rectSizes.width"
            :height="rectSizes.height"
          ></iframe>
        </div>
      </div>
    </HandlerAccessDenied>
  </div>

  <div class="overlay" v-if="isFullPageDashboard">
    <button
      class="close-button"
      @click="isFullPageDashboard = false"
      title="Закрыть"
    >
      <i class="pi pi-times"></i>
    </button>
    <iframe class="overlay-iframe" :src="getToken()" frameborder="0"></iframe>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";

import { useDashbordsStore } from "@/store/analytics/dasboards";
import { Dashboard } from "@/store/analytics/dasboards";
import { isNumeric } from "@/services/utils/Strings";

import CatalogDataSingleInput from "@/widgets/forms/CatalogDataSingleInput.vue";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import HandlerAccessDenied from "@/shared/base/HandlerAccessDenied.vue";
import { useSidebarStore } from "@/store/sidebar/sidebar";

const props = defineProps([
  "store",
  "type",
  "hideExpandButton",
  "defaultWorkbook",
  "defaultDashboard",
]);
const emit = defineEmits(["dashboardSelected"]);

const workbook = ref({
  workbook: null as number | null,
  dashboard: null as Dashboard | null,
});

const isDenied = ref(false);

const isFullPageDashboard = ref(false);
const dashboards = ref([] as Dashboard[]);
const router = useRouter();
const route = useRoute();
const store = useDashbordsStore();
const sidebarStore = useSidebarStore();
const isLoadingData = ref(false);
const token = ref("");
const rectSizes = ref({
  width: 0,
  height: 0,
});

watch(
  () => workbook.value.workbook,
  () => {
    setDashboards();
  }
);

watch(
  () => sidebarStore.isOpen,
  () => setSizes()
);

watch(
  () => workbook.value.dashboard,
  (newValue) => {
    // Уведомляем родительский компонент о выборе дашборда
    emit("dashboardSelected", newValue != null);

    if (newValue && workbook.value.workbook) {
      isDenied.value = false;
      setTimeout(() => {
        setSizes();
      }, 100);
      isLoadingData.value = true;
      store
        .getToken(workbook.value.workbook, newValue.id)
        .then((tokenData) => {
          token.value = tokenData;
          isLoadingData.value = false;
        })
        .catch((err) => {
          if (err.response && err.response.status == 403) {
            isDenied.value = true;
          }
        });
      if (!props.defaultWorkbook || !props.defaultDashboard) {
        router.push({
          name: route.name,
          query: { dashboard: newValue.id, workbook: workbook.value.workbook },
        });
      }
    }
  }
);

// Функция для загрузки и установки дашборда
const loadAndSetDashboard = (
  workbookId: number,
  dashboardId: number,
  isPreset = false
) => {
  props.store.getItems("", [workbookId], true, true).then(() => {
    const foundItem = props.store.itemList.find(
      (item: any) => item.id === workbookId
    );

    if (!foundItem) {
      if (!isPreset) {
        router.push({ name: route.name });
      }
      return;
    }

    store.getWorkbook(workbookId).then((data) => {
      if (data) {
        workbook.value.workbook = workbookId;
        const foundDashboard = data.dashboards.find(
          (item: any) => item.id === dashboardId
        );
        if (foundDashboard) {
          workbook.value.dashboard = foundDashboard;
          if (isPreset) {
            emit("dashboardSelected", true);
          }
        }
      }
    });
  });
};

// Для установки сразу в инпуты единственного доступного дашборда
const loadSingleAvailableDashboard = () => {
  props.store.getItems("", [], true).then(() => {
    if (props.store.itemList.length == 1) {
      workbook.value.workbook = props.store.itemList[0].id;
      if (props.store.itemList[0].dashboards.length == 1) {
        workbook.value.dashboard = props.store.itemList[0].dashboards[0];
      }
    }
  });
};

onMounted(() => {
  // Если переданы предустановленные значения, используем их
  if (props.defaultWorkbook && props.defaultDashboard) {
    loadAndSetDashboard(props.defaultWorkbook, props.defaultDashboard, true);
    return;
  }

  // Иначе смотрим query
  const workbookQ = route.query.workbook;

  if (isNumeric(workbookQ)) {
    const workbookId = parseInt(workbookQ);
    const dashboardQ = route.query.dashboard;

    if (dashboardQ && isNumeric(dashboardQ)) {
      loadAndSetDashboard(workbookId, parseInt(dashboardQ));
    } else {
      // Ставим в инпут только воркбук без дашборда
      props.store.getItems("", [workbookId], true, true).then(() => {
        const foundItem = props.store.itemList.find(
          (item: any) => item.id === workbookId
        );
        if (!foundItem) {
          router.push({ name: route.name });
        }
      });
    }
  } else {
    loadSingleAvailableDashboard();
  }
});

const setDashboards = () => {
  if (workbook.value.workbook) {
    const foundItem = props.store.itemList.find(
      (item: any) => item.id === workbook.value.workbook
    );
    if (foundItem) {
      dashboards.value = foundItem.dashboards;
      return;
    }
  }
  dashboards.value = [];
};

const getToken = () => {
  return `https://datalens.ru/embeds/dash#dl_embed_token=${token.value}`;
};

const calcSizes = () => {
  // Получаем элемент, расстояние которого хотим вычислить
  const element = document.getElementById("dashboard-container");

  if (element) {
    const rect = element.getBoundingClientRect();
    // Получаем размеры окна просмотра
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const distanceToRight = viewportWidth - rect.left;
    const distanceToBottom = viewportHeight - rect.top;

    return {
      right: distanceToRight - 35,
      bottom: distanceToBottom - 45,
    };
  }
};

const setSizes = () => {
  const sizes = calcSizes();
  if (sizes) {
    rectSizes.value.height = sizes.bottom;
    rectSizes.value.width = sizes.right;
  }
};

const tabClick = () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    setSizes();
  }, 300);
};

const setFullPageDashbordSize = () => {
  isFullPageDashboard.value = true;
};

// Экспортируем функцию для вызова извне
defineExpose({
  expandDashboard: () => {
    isFullPageDashboard.value = true;
  },
});

let resizeTimeout: number;
window.addEventListener("resize", function () {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    setSizes();
  }, 1);
});
</script>

<style lang="scss" scoped>
.overlay-iframe {
  width: 100%;
  height: 100%;
}

.overlay {
  display: flex;
  flex-direction: column;
  align-items: end;

  position: fixed;

  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;

  background: #2b2a5c;

  z-index: 1000;
}

.iframe-fullscreen {
  position: absolute;
  left: 0;
  top: 0;

  width: 100%;
}

.dashboards-container {
  margin-top: 77px;
}

.some-inputs {
  max-width: 100%;
}

.input-container {
  max-width: calc(50% - 10px);
  width: calc(50% - 10px);
}

.small-label {
  color: #a19cab;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.42rem;
  padding-bottom: 2px;
  padding-left: 4px;
}

.desc {
  border-radius: 6px;
  border: 1px solid #d0d0d6;
  background: #f7f7fd;
  padding: 8px 12px;
}

.close-button {
  position: absolute;
  top: 15px;
  right: 20px;
  z-index: 1001;
  width: 35px;
  height: 35px;
  border: none;
  border-radius: 6px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  i {
    font-size: 14px;
  }
}
</style>
